import { Injectable } from "@angular/core";
import { EdpModalService } from "@edp-components/edp-modal";
import { NgbModalOptions } from "@ng-bootstrap/ng-bootstrap";
import { IModalData } from "@edp-components/edp-modal/edp-modal.models";
import { NewRequestService } from "@edp-pages/requests-and-operations/services/new-request.service";
import { ShowLoader } from "@edp-store/actions/layout.actions";
import { EdpToastType } from "@edp-components/edp-toaster/edp-toast-type";
import { TranslateService } from "@ngx-translate/core";
import { EdpToastConfig, EdpToasterService } from "@edp-components/edp-toaster/edp-toaster.service";
import { select, Store } from "@ngrx/store";
import { IAppState } from "@edp-store/state/app.state";
import { ITimeSlot } from "@edp-models/requests-and-operations";
import { getSelectedRequest } from "@edp-store/selectors/requests-and-operations.selector";
import { catchError, switchMap, take } from "rxjs/operators";
import { of, throwError } from "rxjs";
import { SetLoadedSlots, SetRequestsAndOperationsDetails } from "@edp-store/actions/requests-and-operations.actions";
import { ActivatedRoute, Router } from "@angular/router";
import { ThrowStmt } from "@angular/compiler";

@Injectable({ providedIn: "root" })
export class NewModalService {
    private modalOptions: NgbModalOptions = {
        windowClass: "edp-dialog-fullscreen",
    };
    private modalData: IModalData | any = {
        title: "Criação de Pedido",
        showClose: true,
    };

    constructor(
        private readonly edpModalService: EdpModalService,
        private readonly newRequestService: NewRequestService,
        private readonly translate: TranslateService,
        private readonly toaster: EdpToasterService,
        private readonly store: Store<IAppState>,
        private readonly router: Router,
        private readonly activatedRoute: ActivatedRoute
    ) {}

    public showNewRequestModal(component, data?) {
        this.modalData = { ...this.modalData, ...data };
        this.modalData.collection = {
            gaClick: !data || (!!data && (data.selectedService === "edphealth" || data.selectedService === "services"))
                ? "requestsAddModal" : "requestsDeleteModal",
            gaEvent: "requestsNewModalStep1"
        };
        this.edpModalService.custom(component, this.modalOptions, this.modalData);
    }

    public getPopupData() {
        return this.modalData;
    }

    public resetPopupData() {
        this.modalData = null;
    }

    public showSubmitModal() {
        let externalCategory: string;
        this.newRequestService
            .getRequestAndService()
            .pipe(take(1))
            .subscribe(request => {
                if (!request) {
                    return;
                }
                externalCategory = request.selectedMotive.externalCategory;
            });

        this.store.dispatch(new ShowLoader(true));
        this.newRequestService
            .submit()
            .pipe(catchError(error => of(this.handleError(error))))
            .subscribe(() => {
                this.store.dispatch(new ShowLoader(false));
                const isFunciona = externalCategory && externalCategory === "02030100";
                const successMessage = isFunciona
                    ? "pt.edp.edponline.requests-and-operations.create-request.submit_request_message_funciona"
                    : "pt.edp.edponline.requests-and-operations.create-request.submit_request_message";
                const toasterConfig = isFunciona ? { timeout: 5000 } : { timeout: -1 };

                this.showToaster(EdpToastType.success, successMessage, toasterConfig);
                this.newRequestService.reloadRequests();
                this.closeModal();
            });
    }

    public submitTimeSlot(timeSlot: ITimeSlot): void {
        this.store.dispatch(new SetLoadedSlots(false));
        this.store
            .pipe(
                select(getSelectedRequest),
                take(1),
                switchMap(request =>
                    this.newRequestService
                        .submitTimeSlots(request.ObjectId, request.Task.type, "OS", timeSlot)
                        .pipe(catchError(error => throwError(error)))
                )
            )
            .subscribe(
                () => {
                    this.showToaster(
                        EdpToastType.success,
                        "pt.edp.edponline.requests-and-operations.create-request.submit_schedule_message"
                    );
                    this.closeModal();
                    this.newRequestService.reloadRequests();
                    this.store.dispatch(new SetRequestsAndOperationsDetails(null));
                    this.router.navigate(["../../pedidos-apoio"], { relativeTo: this.activatedRoute });
                    this.store.dispatch(new SetLoadedSlots(true));
                },
                error => {
                    this.handleError(error);
                    this.closeModal();
                    this.store.dispatch(new SetLoadedSlots(true));
                }
            );
    }

    public closeModal() {
        this.edpModalService.close(false);
        this.newRequestService.resetData();
        this.resetPopupData();
    }

    public dismissModal() {
        this.edpModalService.dismiss(false);
        this.newRequestService.resetData();
        this.resetPopupData();
    }

    private showToaster(type: EdpToastType, message: string, config?: EdpToastConfig): void {
        this.toaster.create(this.translate.instant(message), type, config).show();
    }

    private handleError(error: Error): void {
        this.store.dispatch(new ShowLoader(false));
        this.showToaster(EdpToastType.error, "pt.edp.edponline.requests-and-operations.create-request.submit_request_error_message");
    }
}
